import React from "react";
import moment, { Moment } from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import { renderTimeViewClock, TimePicker } from "@mui/x-date-pickers";

export const pickerBaseSx = {
  width: "100%",

  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
    marginTop: "-0.4rem",
    fontWeight: 600,
  },

  "& .MuiInputLabel-shrink": {
    fontSize: "0.85rem",
    fontWeight: 600,
    marginTop: "0.1rem",
  },

  "& .MuiOutlinedInput-root": {
    fontFamily: '"Titillium Web", sans-serif',
  },

  "& .MuiOutlinedInput-input": {
    padding: "9px 13px",
    fontSize: "0.8rem",
    fontWeight: 600,
    color: "rgb(38, 38, 38)",
  },

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#757575",
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#515151ff",
  },

  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "1px",
  },
};

export const CustomDatePicker: React.FC<{
  value: string | null;
  placeholder?: string;
  sx?: any;
  disabled?: boolean;
  views?: ("day" | "month" | "year")[];
  inputFormat?: string;
  fullWidth?: boolean;
  errorMessage?: string;
  minDate?: any;
  maxDate?: any;
  onChange: (
    value: any,
    timeWithSeconds: any,
    momentObject: Moment | null,
  ) => void;
}> = ({
  value,
  sx,
  placeholder,
  views = ["year", "month", "day"],
  inputFormat = "DD-MM-YYYY",
  fullWidth = true,
  disabled,
  errorMessage,
  minDate,
  maxDate,
  onChange,
}) => {
  return (
    <>
      <DatePicker
        label={placeholder}
        disabled={disabled}
        views={views}
        format={inputFormat}
        minDate={
          minDate && moment(minDate).isValid() ? moment(minDate) : undefined
        }
        maxDate={
          maxDate && moment(maxDate).isValid() ? moment(maxDate) : undefined
        }
        value={value ? moment(value) : null}
        sx={{
          ...(fullWidth && { width: "100%" }),
          ...pickerBaseSx,
          ...sx,
          /* ---------------- DISABLED ---------------- */
          "& .MuiOutlinedInput-input.Mui-disabled": {
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.12)" : "#f8f9fb",
            fontWeight: 600,
            WebkitTextFillColor: "rgba(0, 0, 0, 0.55)",
            cursor: "not-allowed",
          },
        }}
        onChange={(newValue) => {
          if (newValue) {
            const formatted = moment(newValue).format("YYYY-MM-DD");
            onChange(formatted, formatted, newValue);
          } else {
            onChange(null, null, null);
          }
        }}
      />

      {errorMessage && <ErrorMessage value={errorMessage} />}
    </>
  );
};

export const CustomTimePicker: React.FC<{
  value: any;
  sx?: any;
  fullWidth?: boolean;
  onChange: (value: Moment) => void;
  onClear?: () => void;
  disabled?: boolean;
  iconDisable?: boolean;
  format?: "hh:mm A" | string;
}> = (props) => {
  const { value, sx, fullWidth, disabled, iconDisable, format, onClear } =
    props;
  const timeString = value;
  const momentObject = timeString ? moment(timeString, "HH:mm:ss") : null;

  return (
    <TimePicker
      value={momentObject}
      disabled={disabled}
      viewRenderers={{
        hours: renderTimeViewClock,
        minutes: renderTimeViewClock,
        seconds: renderTimeViewClock,
      }}
      format={format}
      onChange={(newValue) => {
        if (newValue) {
          props.onChange(newValue);
        } else {
          if (onClear) {
            onClear();
          }
        }
      }}
      slots={{
        ...(iconDisable && {
          openPickerIcon: () => null,
          openPickerButton: () => null, // <-- fully disables the clickable icon button
        }),
      }}
      slotProps={{
        textField: {
          InputProps: {
            endAdornment: iconDisable ? null : undefined,
          },
        },
      }}
      sx={{
        width: fullWidth ? "100%" : "unset",
        "& .MuiOutlinedInput-input": {
          padding: "9px 13px",
          fontSize: "0.8rem",
          color: "rgb(38, 38, 38)",
        },
        ...sx,
      }}
    />
  );
};
